import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  type ConsentState,
  useCookieConsent,
} from "@/hooks/useCookieConsent";

export function CookieBanner() {
  const {
    hasDecided,
    isPreferencesOpen,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
  } = useCookieConsent();

  const showBanner = !hasDecided && !isPreferencesOpen;

  return (
    <>
      {showBanner && (
        <BannerInitial
          onAccept={acceptAll}
          onReject={rejectAll}
          onCustomize={openPreferences}
        />
      )}
      {isPreferencesOpen && (
        <PreferencesDialog
          onSave={savePreferences}
          onReject={rejectAll}
          onClose={closePreferences}
        />
      )}
    </>
  );
}

/* ---------------------------------------------------------------- */
/*  Initial banner — bottom-right on desktop, stretched on mobile   */
/* ---------------------------------------------------------------- */

function BannerInitial({
  onAccept,
  onReject,
  onCustomize,
}: {
  onAccept: () => void;
  onReject: () => void;
  onCustomize: () => void;
}) {
  const { t } = useTranslation();
  // Exploración (30-10-2026): el formato de RE:PÚBLICO con los colores de
  // RE:CINE (blanca y recta como los desplegables; morada se perdía sobre la
  // portada). Barra NO bloqueante (guía de cookies de la AEPD, 2024): sin
  // fondo oscuro ni bloqueo de scroll, no roba el foco, «Rechazar» con la
  // misma prominencia que «Aceptar» y no se va sin una elección. Móvil: barra
  // abajo a todo el ancho; escritorio: tarjeta abajo a la derecha. Se oculta
  // mientras hay un diálogo de Radix abierto (index.css, .cc-banner).
  const btn =
    "data-voice min-w-0 flex-1 rounded-full border-2 border-rc-navy px-2 py-2.5 text-center text-xs font-bold text-rc-navy transition-[transform,background-color] duration-150 ease-spring hover:bg-rc-lime active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet focus-visible:ring-offset-2";
  return (
    <div
      role="region"
      aria-label={t("cookieBanner.ariaLabel")}
      className="cc-banner fixed inset-x-0 bottom-0 z-[9990] border-t-2 border-rc-navy bg-white px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] text-rc-navy animate-fade-in sm:inset-x-auto sm:bottom-6 sm:right-6 sm:max-w-[420px] sm:border-2 sm:p-5"
    >
      <h2 className="font-display text-xl uppercase leading-none tracking-[-0.01em]">
        {t("cookieBanner.title")}
      </h2>
      <p className="mt-2 text-sm leading-relaxed">
        {t("cookieBanner.short")}{" "}
        <Link to="/cookies" className="font-semibold underline decoration-2 underline-offset-2 hover:text-rc-violet">
          {t("cookieBanner.moreInfo")}
        </Link>
      </p>
      {/* Las tres con la MISMA prominencia (AEPD): mismo estilo, tamaño y peso. */}
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={onAccept} className={btn}>
          {t("cookieBanner.accept")}
        </button>
        <button type="button" onClick={onReject} className={btn}>
          {t("cookieBanner.reject")}
        </button>
        <button type="button" onClick={onCustomize} className={btn}>
          {t("cookieBanner.configure")}
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/*  Preferences modal                                               */
/* ---------------------------------------------------------------- */

function PreferencesDialog({
  onSave,
  onReject,
  onClose,
}: {
  onSave: (state: ConsentState) => void;
  onReject: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("cc-consent");
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<ConsentState>;
        setAnalytics(parsed.analytics === true);
        setAds(parsed.ads === true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = () => onSave({ analytics, ads });

  return (
    <>
      <div
        aria-hidden="true"
        className="fixed inset-0 z-[10000] bg-rc-navy/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-preferences-title"
        ref={dialogRef}
        className="fixed left-1/2 top-1/2 z-[10001] w-[min(560px,92vw)] -translate-x-1/2 -translate-y-1/2 bg-rc-lavender p-0 text-rc-navy animate-fade-in"
      >
        <div className="p-6 space-y-5">
          <header className="space-y-1.5">
            <h2
              id="cookie-preferences-title"
              className="font-display text-3xl uppercase leading-[0.9] tracking-[-0.03em]"
            >
              {t("cookieBanner.preferences.title")}
            </h2>
            <p className="text-sm text-rc-navy/70 leading-[1.6]">
              {t("cookieBanner.preferences.intro")}
            </p>
          </header>

          <div className="space-y-2.5">
            <PreferenceRow
              title={t("cookieBanner.preferences.necessary.title")}
              description={t("cookieBanner.preferences.necessary.description")}
              alwaysOn
            />
            <PreferenceRow
              title={t("cookieBanner.preferences.analytics.title")}
              description={t("cookieBanner.preferences.analytics.description")}
              checked={analytics}
              onChange={setAnalytics}
            />
            <PreferenceRow
              title={t("cookieBanner.preferences.marketing.title")}
              description={t("cookieBanner.preferences.marketing.description")}
              checked={ads}
              onChange={setAds}
            />
          </div>

          <footer className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-1">
            <button
              type="button"
              onClick={onReject}
              className="data-voice inline-flex items-center justify-center rounded-full border-2 border-rc-navy text-rc-navy font-bold text-xs px-6 py-2.5 transition-[transform,background-color] duration-200 ease-spring hover:bg-rc-lime active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet focus-visible:ring-offset-2"
            >
              {t("cookieBanner.rejectAll")}
            </button>
            <button
              type="button"
              onClick={save}
              className="data-voice inline-flex items-center justify-center rounded-full border-2 border-rc-navy bg-rc-navy text-white font-bold text-xs px-6 py-2.5 transition-[transform,box-shadow] duration-200 ease-spring hover:-translate-x-[3px] hover:-translate-y-[3px] hover:shadow-hard-sm-violet active:translate-x-0 active:translate-y-0 active:shadow-none active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet focus-visible:ring-offset-2"
            >
              {t("cookieBanner.savePreferences")}
            </button>
          </footer>
        </div>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- */
/*  Preference row with iOS-style switch                            */
/* ---------------------------------------------------------------- */

function PreferenceRow({
  title,
  description,
  checked,
  alwaysOn,
  onChange,
}: {
  title: string;
  description: string;
  checked?: boolean;
  alwaysOn?: boolean;
  onChange?: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  const isOn = alwaysOn || checked;
  return (
    <div className="flex items-center justify-between gap-4 border-t-2 border-rc-navy px-1 py-3.5">
      <div className="min-w-0 space-y-0.5">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-rc-navy/70 leading-[1.5]">{description}</div>
      </div>
      {alwaysOn ? (
        <span className="data-voice shrink-0 text-[11px] font-bold text-rc-violet">
          {t("cookieBanner.preferences.alwaysOn")}
        </span>
      ) : (
        <Switch checked={!!isOn} onChange={onChange!} label={title} />
      )}
    </div>
  );
}

function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={[
        "relative shrink-0 h-6 w-11 rounded-full transition-colors duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet focus-visible:ring-offset-2",
        checked ? "bg-rc-navy" : "bg-rc-navy/20",
      ].join(" ")}
    >
      <span
        className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ease-spring"
        style={{ transform: checked ? "translateX(20px)" : "translateX(0)" }}
      />
    </button>
  );
}
